import React, { useEffect } from 'react';

import { useMutation } from '@betterment/js-runtime';
import { Button, Card, Dialog, GuidanceBox, H1, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { DeferralProjection } from './DeferralProjection/DeferralProjection';
import { DeferralProjectionDetailsDialog } from './DeferralProjection/DeferralProjectionDetailsDialog';
import { DeferralProjectionLoading } from './DeferralProjectionLoading/DeferralProjectionLoading';

import { trackClick } from '../../features/track';
import { currentYear } from '../../FourOhOneKDeferralSettings/models';
import { urlBuilder } from '../../Shared/urlBuilder';
import { useEmployersContributionConfigQuery } from '../hooks';
import { DeferralSettings, ProjectionRequestParams, ProjectionResponseData } from '../types';
import { toFormattedMoney } from '../utils';

interface DeferralProjectionCardProps {}

const toDeferralSettingsInput = (formInput: DeferralSettings['base']['traditional']) => {
  const { type, value } = formInput;

  return {
    type,
    payroll_deferral_amount_cents: type === 'amount' ? value : null,
    payroll_deferral_percent: type === 'percent' ? value : null,
  };
};

export const DeferralProjectionCard: React.FC<DeferralProjectionCardProps> = () => {
  const { employerId } = useParams<{ employerId: string }>();
  const { employee, fourOhOneKContributionsThisYear } = useEmployersContributionConfigQuery(employerId!);
  const [getDeferralProjection, { data: projections }] = useMutation<ProjectionResponseData, ProjectionRequestParams>(
    urlBuilder.apiV2Annual401kDeferralProjection.absolute()
  );
  const { values } = useFormikContext<DeferralSettings>();
  const [debouncedInputs] = useDebounce(values, 500);
  const maxOutThresholdCents = 500;

  useEffect(() => {
    if (employee === undefined || fourOhOneKContributionsThisYear === undefined) {
      return;
    }

    void getDeferralProjection({
      annualSalaryCents: employee.salary.cents,
      contributionLimitAmountCents: fourOhOneKContributionsThisYear.taxYear.fourOhOneKContributionLimit.cents,
      payFrequency: employee.payFrequency,
      remainingAnnualPayrolls: employee.remainingAnnualPayrolls,
      ytdContributionAmountCents: fourOhOneKContributionsThisYear.result.amount.cents,
      traditionalDeferral: toDeferralSettingsInput(debouncedInputs.base.traditional),
      rothDeferral: debouncedInputs.base.roth ? toDeferralSettingsInput(debouncedInputs.base.roth) : null,
    });
  }, [debouncedInputs, employee, fourOhOneKContributionsThisYear, getDeferralProjection]);

  let isOnTrackToMaxOut;

  if (projections && fourOhOneKContributionsThisYear) {
    const contributionLimit = fourOhOneKContributionsThisYear.taxYear.fourOhOneKContributionLimit.cents;
    isOnTrackToMaxOut = projections.totalContributionAmountCents >= contributionLimit - maxOutThresholdCents;
  }

  return (
    <Card css={{ border: '1px solid rgba(199, 199, 199, 1)', p: 'space.300' }}>
      <Card.Header css={{ display: 'flex', justifyContent: 'space-between' }}>
        <H1 variant="title200">{currentYear()} contributions</H1>
        <Dialog overlay="default">
          <Dialog.Trigger asChild={true}>
            <Button variant="textPrimary">View details</Button>
          </Dialog.Trigger>
          {projections && fourOhOneKContributionsThisYear && (
            <DeferralProjectionDetailsDialog
              projections={projections}
              currentYearContributions={fourOhOneKContributionsThisYear}
            />
          )}
        </Dialog>
      </Card.Header>
      <Separator css={{ my: 'space.300' }} />
      <Card.Content>
        {fourOhOneKContributionsThisYear === undefined ? (
          <DeferralProjectionLoading />
        ) : (
          <DeferralProjection
            ytdContribution={fourOhOneKContributionsThisYear.result.amount.cents}
            projectedFutureContribution={Math.max(projections?.futureContributionAmountCents || 0, 0)}
            projectedTotalContribution={projections?.totalContributionAmountCents || 0}
            contributionLimit={fourOhOneKContributionsThisYear.taxYear.fourOhOneKContributionLimit.cents}
          />
        )}
        {isOnTrackToMaxOut && (
          <GuidanceBox variant="b" css={{ my: 'space.200' }}>
            <GuidanceBox.Icon />
            <GuidanceBox.Content
              css={{
                display: 'grid',
                gap: 'space.100',
              }}
            >
              <Text variant="text50">
                You are on track to max out your 401(k)! You’re{' '}
                {toFormattedMoney(fourOhOneKContributionsThisYear?.result.amountRemainingToLimit.cents)} away from{' '}
                maxing out your total 401(k) contributions in {currentYear()}*
              </Text>
            </GuidanceBox.Content>
          </GuidanceBox>
        )}
        {employee && (
          <Text variant="text25">
            *Estimated calculation, no guarantee is being made. This estimate does not include contributions from your
            employer or bonus.{' '}
            <SambaAutoLink
              onClick={() => trackClick({ name: 'PayrollAssumptionEdit', flow: 'EditDeferralSetting' })}
              to={urlBuilder.appPayrollAssumptions.absolute({
                employeeId: employee.legacyId,
                return_to: window.location.pathname,
                skip_redirect: 1,
              })}
            >
              Review your information
            </SambaAutoLink>
          </Text>
        )}
      </Card.Content>
    </Card>
  );
};
